import { Component, DestroyRef, EventEmitter, Injector, Input, OnInit, Output, Signal, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MerchantService } from '@galaxy/billing';
import { Frequency } from '@vendasta/galaxy/frequency';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { AppPricingApiService, GetMultiPricingRequest, PartnerApiService } from '@vendasta/marketplace-apps';
import { MarketplacePackagesApiService } from '@vendasta/marketplace-packages';
import { BehaviorSubject, Observable, combineLatest, filter, of, startWith, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { LISTING_BUILDER_ITEM_ID } from '../item-selector/constants';
import { ItemPricingTableDataSource } from './item-pricing-table.data-source';
import {
  ItemFeeRow,
  ItemRow,
  ItemRowChangeEvent,
  PriceSummary,
  PriceTotal,
  RETAIL_PRICING_CONTEXT,
  WHOLESALE_PRICING_CONTEXT,
} from './item-pricing-table.interface';
import {
  calculateTotalRetailPrice,
  calculateTotalWholesalePrice,
  convertHTMLControlValueToNumber,
  parseItemFees,
  updatePercentageManagementFee,
} from './item-pricing-table.utils';
import { MultiActivatableDialogComponent } from './multi-activatable-dialog/multi-activatable-dialog.component';
import { parseWholesaleItemPrice } from '../common/item-price/wholesale';
import { parseRetailItemPrice } from '../common/item-price/retail';
import {
  BillingTermDialogComponent,
  BillingTermDialogInput,
  BillingTermDialogOutput,
} from '../billing-term-dialog/billing-term-dialog.component';
import {
  galaxyFrequencyToRevenuePeriod,
  revenuePeriodToGalaxyFrequency,
} from '../billing-term-dialog/sales-to-galaxy-frequency.pipe';
import { InventoryItemWithPricing } from '../common/inventory-item';

export interface ItemPricingTableConfig {
  partnerId: string;
  marketId: string;
  businessId?: string;
  selectionEditable?: boolean;
  quantityEditable?: boolean;
  billingTermsEnabled?: boolean;
  hideColumns?: ItemPricingTableColumnID[];
  hidePriceSummary?: boolean;
  hideBorder?: boolean;
}

export type ItemPricingTableColumnID =
  | 'name'
  | 'quantity'
  | 'wholesale_price'
  | 'retail_price'
  | 'retail_frequency'
  | 'retail_price_total'
  | 'menu';

const ITEM_PRICING_TABLE_COLUMNS: GalaxyColumnDef[] = [
  {
    id: 'name',
    title: 'Name',
  },
  {
    id: 'quantity',
    title: 'Quantity',
  },
  {
    id: 'wholesale_price',
    title: 'Wholesale',
  },
  {
    id: 'retail_price',
    title: 'Retail',
  },
  {
    id: 'retail_frequency',
    title: 'Frequency',
  },
  {
    id: 'retail_price_total',
    title: 'Retail Total',
  },
  {
    id: 'menu',
    title: 'Menu',
  },
];

@Component({
  selector: 'inventory-ui-item-pricing-table',
  templateUrl: './item-pricing-table.component.html',
  styleUrls: ['./item-pricing-table.component.scss'],
})
export class ItemPricingTableComponent implements OnInit {
  @Input() config!: ItemPricingTableConfig;
  private _items$$ = new BehaviorSubject<InventoryItemWithPricing[]>([]);
  @Input() set items(value: InventoryItemWithPricing[]) {
    this._items$$.next(value);
  }

  @Output() updatedItemRow = new EventEmitter<ItemRowChangeEvent>();
  @Output() valid = new EventEmitter<boolean>();
  @Output() wholesaleTotal = new EventEmitter<number>();

  private _invalidPriceItems: string[] = [];

  readonly listingBuilderItemId = LISTING_BUILDER_ITEM_ID;
  dataSource!: ItemPricingTableDataSource;
  tableDataSource!: GalaxyDataSource<ItemRow>;
  columns: GalaxyColumnDef[] = ITEM_PRICING_TABLE_COLUMNS;
  Frequency = Frequency;
  priceTotals$!: Observable<PriceTotal>;
  showManagementFees = true;
  showRetailFrequencySuffix = false;

  private injector = inject(Injector);
  private wholesaleSummary$!: Observable<PriceSummary>;
  private retailSummary$!: Observable<PriceSummary>;
  public wholesaleSummary!: Signal<PriceSummary>;
  public retailSummary!: Signal<PriceSummary>;

  constructor(
    private dialog: MatDialog,
    private readonly partnerApiService: PartnerApiService,
    private readonly appPricingApiService: AppPricingApiService,
    private readonly merchantService: MerchantService,
    private readonly marketplacePackagesApiService: MarketplacePackagesApiService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    if (this.config?.hideColumns?.length) {
      this.config.hideColumns.forEach((col) => {
        if (col === 'wholesale_price') {
          this.showManagementFees = false;
        }
        if (col === 'retail_frequency') {
          this.showRetailFrequencySuffix = true;
        }

        this.columns = this.columns.filter((c) => c.id !== col);
      });
    }

    this.dataSource = new ItemPricingTableDataSource(
      this._items$$.asObservable(),
      this.config?.partnerId,
      this.config?.marketId,
      this.config?.businessId ?? '',
      this.appPricingApiService,
      this.partnerApiService,
      this.merchantService,
      this.marketplacePackagesApiService,
    );

    this.tableDataSource = new GalaxyDataSource<ItemRow>(this.dataSource);
    this.wholesaleSummary$ = this.getPriceSummaryByContext('wholesale');
    this.retailSummary$ = this.getPriceSummaryByContext('retail');

    this.wholesaleSummary = toSignal(this.wholesaleSummary$, {
      initialValue: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
      injector: this.injector,
    });
    this.retailSummary = toSignal(this.retailSummary$, {
      initialValue: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
      injector: this.injector,
    });

    this.priceTotals$ = combineLatest([this.wholesaleSummary$, this.retailSummary$]).pipe(
      map(([wholesale, retail]) => {
        return {
          wholesaleTotal: wholesale,
          retailTotal: retail,
          initialized: true,
        };
      }),
      startWith({
        wholesaleTotal: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
        retailTotal: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
        initialized: false,
      }),
    );

    // TODO - clean this up when the ordering workflow is refactored to initialize package pricing
    this.tableDataSource.dataMembers$
      .pipe(
        filter((rows: ItemRow[]) => rows.length > 0),
        take(1),
      )
      .subscribe((rows) => {
        rows.forEach((row) => {
          // explicitly publish changes for packages to initialize pricing
          if (row.type === 'package') {
            this.publishChanges(row);
          }
        });
      });
    this.wholesaleSummary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((wholesale) => {
      this.wholesaleTotal.emit(wholesale.oneTime);
    });
  }

  onQuantityChange(changeEvent: Event, item: ItemRow): void {
    const htmlElement = changeEvent.target as HTMLInputElement;
    let quantity = htmlElement.valueAsNumber;
    if (quantity < 1) {
      htmlElement.value = '1';
      quantity = 1;
    }
    this.updateQuantity(item, quantity);
  }

  updateQuantity(item: ItemRow, quantity: number): void {
    item.quantity = quantity;
    if (quantity > 0) {
      item.wholesalePricing.frequencyPrices.forEach((fp) => (fp.total = fp.price * item.quantity));
      item.retailPricing.total = item.retailPricing.price * item.quantity;
    }
    const eventType = quantity > 0 ? 'update' : 'delete';
    this.publishChanges(item, eventType);
  }

  updateRetailPricing(item: ItemRow, value: number) {
    item.retailPricing.price = value;
    item.retailPricing.total = value * item.quantity;
  }

  updateWholesalePricing(item: ItemRow, frequency: Frequency, value: number) {
    const frequencyPrice = item.wholesalePricing.frequencyPrices.find((fp) => fp.frequency === frequency);
    if (!frequencyPrice) return;

    const oldWholeSalePrice = frequencyPrice.price;
    frequencyPrice.price = value;
    frequencyPrice.total = value * item.quantity;
    updatePercentageManagementFee(item, oldWholeSalePrice);
    this.validateWholesalePrice(item);
  }

  updateSetupFee(item: ItemRow, value: number) {
    item.fees.forEach((fee) => {
      if (fee.type === 'setup') {
        fee.retailAmount = value;
        fee.retailAmountTotal = fee.retailAmount * item.quantity;
      }
    });
  }

  validateWholesalePrice(item: ItemRow) {
    const invalidPrice = item.wholesalePricing.frequencyPrices.every(
      (fp) => fp.minimumPrice && fp.minimumPrice >= fp.price,
    );
    if (invalidPrice) {
      if (!this._invalidPriceItems.includes(item.itemId)) {
        this._invalidPriceItems.push(item.itemId);
        this.valid.emit(this._invalidPriceItems.length === 0);
      }
    } else {
      if (this._invalidPriceItems.includes(item.itemId)) {
        this._invalidPriceItems.splice(this._invalidPriceItems.indexOf(item.itemId), 1);
        this.valid.emit(this._invalidPriceItems.length === 0);
      }
    }
  }

  resetItemPricing(item: ItemRow): void {
    const request = new GetMultiPricingRequest({
      appIds: [item.itemId],
      pricingContexts: {
        paths: [WHOLESALE_PRICING_CONTEXT, RETAIL_PRICING_CONTEXT],
      },
      partnerId: this.config?.partnerId,
      marketId: this.config?.marketId,
    });
    this.appPricingApiService
      .getMultiPricing(request)
      .pipe(
        take(1),
        map((response) => response.appPrices[0]),
      )
      .subscribe((appPrices) => {
        // exclude custom pricing to make sure marketplace prices are applied
        const itemWithoutPricing: ItemRow = { ...item, pricing: undefined };
        item.wholesalePricing = parseWholesaleItemPrice({ appPrices, item: itemWithoutPricing });
        item.retailPricing = parseRetailItemPrice({
          appPrices,
          item: itemWithoutPricing,
          marketCurrency: item.retailPricing.currency,
        });
        item.fees = parseItemFees(appPrices, itemWithoutPricing);
        this.publishChanges(item);
      });
  }

  onPriceChange(
    changeEvent: Event,
    item: ItemRow,
    priceType: 'wholesale' | 'retail' | 'setupfee',
    frequency: Frequency = Frequency.ONE_TIME,
  ): void {
    const htmlInputValue = convertHTMLControlValueToNumber((<HTMLInputElement>changeEvent.target).value);
    const price = this.convertDollarsToCents(htmlInputValue);
    if (priceType === 'wholesale') this.updateWholesalePricing(item, frequency, price);
    if (priceType === 'retail') this.updateRetailPricing(item, price);
    if (priceType === 'setupfee') this.updateSetupFee(item, price);
    this.publishChanges(item);

    if (priceType !== 'wholesale') {
      this.confirmRetailChange(item)
        .pipe(take(1))
        .subscribe((changesApproved: boolean) => {
          item.retailChangeConfirmed = true;
          if (!changesApproved) {
            this.resetItemPricing(item);
          }
        });
    }
  }

  handleFrequencyChange(frequency: Frequency, item: ItemRow): void {
    item.retailPricing.frequency = frequency;
    this.publishChanges(item);
  }

  confirmRetailChange(item: ItemRow): Observable<boolean> {
    if (item.isMultiactivatable && !item.retailChangeConfirmed) {
      return this.dialog
        .open(MultiActivatableDialogComponent, {
          width: '400px',
        })
        .afterClosed()
        .pipe(map(Boolean));
    }
    return of(true);
  }

  convertDollarsToCents(value: number): number {
    return Math.round(value * 100);
  }

  removeSetupFee(item: ItemRow, fee: ItemFeeRow): void {
    this.updateSetupFee(item, 0);
    item.fees.splice(item.fees.indexOf(fee), 1);
    this.publishChanges(item);
  }

  addSetupFee(itemToUpdate: ItemRow): void {
    itemToUpdate.fees.unshift({
      retailAmount: 0,
      amountType: 'fixed',
      label: 'Setup fee',
      type: 'setup',
      wholesaleAmount: 0,
      frequency: Frequency.ONE_TIME,
    });
  }

  onRemoveItem(item: ItemRow): void {
    this.updateQuantity(item, 0);
  }

  publishChanges(item: ItemRow, action: 'update' | 'add' | 'delete' = 'update') {
    this.updatedItemRow.emit({ item, action });
    this.dataSource.refreshItemRows();
  }

  getPriceSummaryByContext = (type: 'wholesale' | 'retail'): Observable<PriceSummary> => {
    return this.tableDataSource.dataMembers$.pipe(
      filter((dataMembers) => dataMembers.length > 0),
      map((dataMembers) => {
        if (type === 'wholesale') return calculateTotalWholesalePrice(dataMembers);
        return calculateTotalRetailPrice(dataMembers);
      }),
      startWith({ oneTime: 0, monthly: 0, yearly: 0, currency: '' }),
    );
  };

  onEditBillingTerm(item: ItemRow): void {
    this.dialog
      .open(BillingTermDialogComponent, {
        data: {
          itemId: item.itemId,
          name: item.name,
          iconUrl: item.iconUrl,
          revenueComponent: {
            revenue: item.retailPricing.total * 100,
            period: galaxyFrequencyToRevenuePeriod(item.retailPricing.frequency),
          },
          startDate: item.billingTerm?.startDate,
          endDate: item.billingTerm?.endDate,
        } as BillingTermDialogInput,
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '500px',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((dialogResult: BillingTermDialogOutput) => {
        if (!dialogResult) {
          return;
        }

        item.billingTerm = {
          startDate: dialogResult.startDate,
          endDate: dialogResult.endDate ?? undefined,
        };
        item.retailPricing.frequency = revenuePeriodToGalaxyFrequency(dialogResult.billingFrequency);
        this.publishChanges(item);
        if (dialogResult.addAnotherTerm) {
          this.onAddBillingTerm(item);
        }
      });
  }

  onAddBillingTerm(item: ItemRow) {
    this.publishChanges(item, 'add');
  }
}
